import { Borders, Row, Workbook, Worksheet } from "exceljs"
import moment from "moment"
import { BalanceSheetReportType } from "../types"

type HandleExportExcel = {
  data: BalanceSheetReportType[]
  companyName?: string
  date?: string,
  dateTo?: string
}

export const handleExportExcelBugetManager = async ({
  data,
  companyName,
  date,
  dateTo
}: HandleExportExcel) => {
  const workbook = new Workbook()
  const worksheet = workbook.addWorksheet("Sheet 1")
  const parents = data.filter((item) => item.parent_id === -1)

  worksheet.views = [{ showGridLines: false }]

  // Title
  makeTitle(worksheet, companyName, date, dateTo)

  // Header
  const headers = worksheet.addRow([
    "ACCOUNT CODE",
    "ACCOUNT NAME",
    `${moment(date).format("MM/YYYY")} - ${moment(dateTo).format("MM/YYYY")}`,
    `${moment(date).format("MM/YYYY")} - ${moment(dateTo).format("MM/YYYY")}`,
    "VARIANCE",
    "VARIANCE %"
  ])
  headers.font = { bold: true }
  headers.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD700" }
    }
    cell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } }
    }
  })

  const mapData = parents?.map((item) => {
    const hierarchy = buildHierarchy(data, item.coa_id)
    return {
      ...item,
      children: hierarchy
    }
  })

  makeRows(mapData, worksheet)

  // create xlsx
  await workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `budget_variance.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  })
}

// Helpers
export const buildHierarchy = (
  data: BalanceSheetReportType[],
  parentId: number | null
): BalanceSheetReportType[] => {
  return data
    .filter((item) => item.parent_id === parentId)
    .map((item) => ({
      ...item,
      children: buildHierarchy(data, item.coa_id)
    }))
}

export const makeTitle = (worksheet: Worksheet, companyName?: string, date?: string, dateTo?: string) => {
  const titleCell = worksheet.getCell(1, 2)
  titleCell.value = "BUDGET VARIANCE"

  titleCell.alignment = {
    vertical: "middle",
    horizontal: "left",
    wrapText: true
  }
  titleCell.font = { bold: true, size: 15 }

  worksheet.getColumn(2).width = 20

  const companyNameCell = worksheet.addRow([companyName?.toUpperCase()])

  const dateCell = worksheet.addRow([
    `For the month from ${moment(date).locale("en-us").format("MMMM YYYY")} to ${moment(dateTo).locale("en-us").format("MMMM YYYY")}`
  ])

  const currentRowIdx = worksheet.rowCount
  const endColumnIdx = worksheet.columnCount

  worksheet.mergeCells(currentRowIdx, 1, currentRowIdx + 1, endColumnIdx)

  companyNameCell.eachCell((cell) => {
    cell.alignment = {
      vertical: "middle",
      horizontal: "left",
      wrapText: true
    }
    cell.font = { bold: true, size: 15 }
  })

  dateCell.eachCell((cell) => {
    cell.alignment = {
      vertical: "middle",
      horizontal: "left",
      wrapText: true
    }
    cell.font = { size: 12 }
  })
}

export const makeRows = (data: any, worksheet: Worksheet) => {
  let itemRow: Row

  const cellBorder: Partial<Borders> = {
    top: { style: "thin", color: { argb: "000000" } },
    left: { style: "thin", color: { argb: "000000" } },
    bottom: { style: "thin", color: { argb: "000000" } },
    right: { style: "thin", color: { argb: "000000" } }
  }

  data.forEach((item: any) => {
    const actual = item.actual?.[0] ?? ""
    const budget = item.budget?.[0] ?? ""
    const variance = item.variance?.[0] ?? ""
    const absVariance = item.variance?.[0] !== "" ? Math.abs(item.variance[0]) : ""
    const percentVariance = item.percent_variance?.[0]
      ? `${item.percent_variance[0]} %`
      : ""

    if (item.header_level === 3 || ["NET PROFIT", "NET ASSETS"].includes(item.coa_name)) {
      itemRow = worksheet.addRow([
        item.coacode,
        item.coa_name,
        actual,
        budget,
        absVariance,
        percentVariance
      ])
      itemRow.font = { bold: ["NET PROFIT", "NET ASSETS"].includes(item.coa_name) }
    } else {
      itemRow = worksheet.addRow([
        item.coacode,
        item.coa_name,
        "", "", "", ""
      ])
      itemRow.font = { bold: true }
    }

    itemRow.eachCell((cell) => {
      cell.border = cellBorder
    })

    if (variance !== "") {
      const varianceCell = itemRow.getCell(5)

      if (variance > 0) {
        varianceCell.font = { color: { argb: "008000" } }
      } else if (variance < 0) {
        varianceCell.font = { color: { argb: "FF0000" } }
      }
    }

    if (item.children?.length > 0) {
      makeRows(item.children, worksheet)
    }

    if (
      item.coa_level !== 3 &&
      !["NET PROFIT", "NET ASSETS"].includes(item.coa_name)
    ) {

      const totalActual = item.actual?.reduce((a: number, b: number) => a + b, 0) || 0
      const totalBudget = item.budget?.reduce((a: number, b: number) => a + b, 0) || 0
      const totalVariance = item.variance?.reduce((a: number, b: number) => a + b, 0) || 0
      const totalPercentVariance = totalBudget !== 0 ? (totalVariance / totalBudget) * 100 : 0

      const totalRow = worksheet.addRow([
        `Total ${item.coa_name}`,
        "",
        totalActual,
        totalBudget,
        Math.abs(totalVariance),
        totalPercentVariance
      ])
      totalRow.font = { bold: true }

      totalRow.eachCell((cell) => {
        cell.border = cellBorder
      })

      const totalVarianceCell = totalRow.getCell(5)
      if (totalVariance > 0) {
        totalVarianceCell.font = { color: { argb: "008000" }, bold: true }
      } else if (totalVariance < 0) {
        totalVarianceCell.font = { color: { argb: "FF0000" }, bold: true }
      }
    }
  })
}
